import React from 'react';
import { ISearchState } from '../table-browser';
import { WhereQueryBuilder } from './WhereQueryBuilder';

interface IQuerySearchComponentProps {
    searchState: ISearchState;
    updateSearchState: (state: ISearchState) => void;
    search: () => void;
}

export const QuerySearchComponent: React.FC<IQuerySearchComponentProps> = ({ searchState, updateSearchState, search }) => {
    const [colInputVal, setColInputVal] = React.useState<string>('');

    const updateSchema = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchState({ ...searchState, schema: e.target.value });
    };

    const updateTable = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchState({ ...searchState, table: e.target.value });
    };

    const updateOrderBy = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchState({ ...searchState, orderBy: e.target.value });
    };

    const updateIsAscending = () => updateSearchState({ ...searchState, isAscending: !searchState.isAscending });

    // const updateTop = (e: React.ChangeEvent<HTMLSelectElement>) =>
    //     updateSearchState({ ...searchState, top: parseInt(e.target.value, 10), skip: 0 });

    const updateColInputVal = (e: React.ChangeEvent<HTMLInputElement>) => setColInputVal(e.target.value);

    const updatePassword = (e: React.ChangeEvent<HTMLInputElement>) => updateSearchState({ ...searchState, password: e.target.value });

    const handleDeleteColumn = (col: string) => {
        updateSearchState({
            ...searchState,
            columns: searchState.columns.filter(c => c !== col)
        });
    };

    const addCol = () => {
        const trimmedValue = colInputVal.trim();

        if (!searchState.columns.includes(trimmedValue) && trimmedValue !== '') {
            updateSearchState({ ...searchState, columns: [...searchState.columns, trimmedValue] });
            setColInputVal('');
        }
    };

    const shouldSubmitColInput = (e: React.KeyboardEvent) => {
        if (e.key.toLowerCase() === 'enter') {
            e.preventDefault();
            addCol();
        }
    };

    const updateWhere = (where: ISearchState['where']) => {
        updateSearchState({
            ...searchState,
            where,
            skip: 0
        });
    };

    return (
        <div className='table-browser-controls'>
            <div className='table-browser-controls__section'>
                <div className='table-browser-controls__section-header'>
                    <h2 className='table-browser-controls__section-title'>Connection</h2>
                    <p className='table-browser-controls__section-description'>Enter the access password and target table.</p>
                </div>

                <div className='table-browser-controls__grid table-browser-controls__grid--three'>
                    <div className='table-browser-controls__field'>
                        <label className='table-browser-controls__label' htmlFor='password'>
                            Password
                        </label>
                        <input
                            type='password'
                            id='password'
                            name='Password'
                            value={searchState.password}
                            onChange={updatePassword}
                            className='table-browser-controls__input'
                            placeholder='Enter password'
                        />
                    </div>

                    <div className='table-browser-controls__field'>
                        <label className='table-browser-controls__label' htmlFor='schema'>
                            Table Schema
                        </label>
                        <input
                            type='text'
                            name='schema'
                            value={searchState.schema}
                            onChange={updateSchema}
                            className='table-browser-controls__input'
                            id='schema'
                            placeholder='ex: ext'
                        />
                    </div>

                    <div className='table-browser-controls__field'>
                        <label className='table-browser-controls__label' htmlFor='table'>
                            Table Name
                        </label>
                        <input
                            type='text'
                            id='table'
                            name='table'
                            value={searchState.table}
                            onChange={updateTable}
                            className='table-browser-controls__input'
                            placeholder='ex: Gdnsupersession'
                        />
                    </div>
                </div>
            </div>

            <div className='table-browser-controls__section'>
                <div className='table-browser-controls__section-header'>
                    <h2 className='table-browser-controls__section-title'>Columns</h2>
                    <p className='table-browser-controls__section-description'>
                        Add * to return the default server-side column set, or add specific columns.
                    </p>
                </div>

                <div className='table-browser-controls__inline-field'>
                    <div className='table-browser-controls__field table-browser-controls__field--grow'>
                        <label className='table-browser-controls__label' htmlFor='column'>
                            Add Column
                        </label>
                        <input
                            type='text'
                            id='column'
                            name='Columns'
                            value={colInputVal}
                            onKeyDown={shouldSubmitColInput}
                            onChange={updateColInputVal}
                            className='table-browser-controls__input'
                            placeholder='ex: ITEMID'
                        />
                    </div>

                    <button className='table-browser-controls__secondary-button' type='button' onClick={addCol}>
                        Add Column
                    </button>
                </div>

                {searchState.columns.length > 0 && (
                    <div className='table-browser-controls__chips' aria-label='Selected columns'>
                        {searchState.columns.map(col => (
                            <button
                                className='table-browser-controls__chip'
                                key={col}
                                type='button'
                                onClick={() => handleDeleteColumn(col)}
                                title='Remove column'
                            >
                                <span>{col}</span>
                                <span className='table-browser-controls__chip-remove'>×</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className='table-browser-controls__section'>
                <WhereQueryBuilder where={searchState.where} updateWhere={updateWhere} />
            </div>

            <div className='table-browser-controls__section'>
                <div className='table-browser-controls__section-header'>
                    <h2 className='table-browser-controls__section-title'>Sorting and paging</h2>
                    <p className='table-browser-controls__section-description'>Control result ordering and page size.</p>
                </div>

                <div className='table-browser-controls__grid table-browser-controls__grid--three'>
                    <div className='table-browser-controls__field'>
                        <label className='table-browser-controls__label' htmlFor='orderBy'>
                            Order By
                        </label>
                        <input
                            type='text'
                            id='orderBy'
                            name='Order By'
                            value={searchState.orderBy}
                            onChange={updateOrderBy}
                            className='table-browser-controls__input'
                            placeholder='ex: RECID'
                        />
                    </div>

                    {/* <div className='table-browser-controls__field'>
                        <label className='table-browser-controls__label' htmlFor='numResults'>
                            Results per page
                        </label>
                        <select
                            id='numResults'
                            name='Number of Results'
                            value={searchState.top}
                            onChange={updateTop}
                            className='table-browser-controls__select'
                        >
                            <option value='25'>25</option>
                            <option value='50'>50</option>
                            <option value='100'>100</option>
                        </select>
                    </div> */}

                    <div className='table-browser-controls__checkbox-field'>
                        <input
                            className='table-browser-controls__checkbox'
                            type='checkbox'
                            id='isAscending'
                            name='is Ascending'
                            checked={searchState.isAscending}
                            onChange={updateIsAscending}
                        />
                        <label className='table-browser-controls__checkbox-label' htmlFor='isAscending'>
                            Sort ascending
                        </label>
                    </div>
                </div>
            </div>

            <div className='table-browser-controls__actions'>
                <button className='table-browser-controls__primary-button' onClick={search} type='button'>
                    Search table
                </button>
            </div>
        </div>
    );
};
